"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getAppErrorCopy } from "@/lib/appError";
import { IconAlertTriangle, IconHome, IconRefresh } from "@tabler/icons-react";
import { useMemo } from "react";

function browserLocale() {
  if (typeof document === "undefined") return "de";
  return document.documentElement.lang || "de";
}

function currentPortalHomeHref() {
  if (typeof window === "undefined") return "/user";
  return window.location.pathname.startsWith("/user") ? "/user" : "/";
}

function reloadPage() {
  window.location.reload();
}

export default function AppErrorFallback({
  error,
  reset,
  locale,
  homeHref,
  showTechnicalDetails = process.env.NODE_ENV !== "production",
}: {
  error?: (Error & { digest?: string }) | null;
  reset: () => void;
  locale?: string | null;
  homeHref?: string;
  showTechnicalDetails?: boolean;
}) {
  const resolvedLocale = locale ?? browserLocale();
  const copy = useMemo(
    () => getAppErrorCopy(error, resolvedLocale),
    [error, resolvedLocale],
  );
  const resolvedHomeHref = homeHref ?? currentPortalHomeHref();
  const secondaryAction = resolvedHomeHref.startsWith("/user")
    ? copy.secondaryAction
    : copy.homeAction;
  const primaryAction = copy.kind === "chunk-load" ? reloadPage : reset;

  return (
    <div
      className="flex min-h-[70vh] flex-col items-center justify-center px-4 py-16 md:py-24"
      role="alert"
    >
      <Card className="w-full max-w-lg">
        <CardContent className="flex flex-col items-center gap-5 p-8 text-center">
          <div className="bg-destructive/10 flex size-14 items-center justify-center rounded-full">
            <IconAlertTriangle className="text-destructive size-7" />
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-destructive text-xs font-semibold tracking-wide uppercase">
              {copy.eyebrow}
            </p>
            <h1 className="text-foreground text-xl font-semibold">
              {copy.title}
            </h1>
            <p className="text-muted-foreground text-sm leading-6">
              {copy.description}
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button size="sm" onClick={primaryAction}>
              <IconRefresh className="size-4" />
              {copy.primaryAction}
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href={resolvedHomeHref}>
                <IconHome className="size-4" />
                {secondaryAction}
              </a>
            </Button>
          </div>
          {showTechnicalDetails && error?.message && (
            <details className="text-muted-foreground w-full text-left text-xs">
              <summary className="cursor-pointer text-center">
                Technische Details
              </summary>
              <pre className="bg-muted mt-2 max-h-32 overflow-auto rounded-md p-3 whitespace-pre-wrap">
                {error.message}
              </pre>
            </details>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
